import React, { Fragment, useMemo, useState, useRef } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from "@heroicons/react/20/solid";

function parseZIndex(zIndex) {
  // supporta "z-20" e "z-[80]"
  const m = String(zIndex || "").match(/\d+/);
  return m ? Number(m[0]) : 20;
}

const ComboboxAutocomplete = ({
  options,
  bgActiveColor,
  textActiveColor,
  placeholder,
  onChange,
  value = null,
  positionOptions,
  zIndex = "z-60",
  openDirection = "bottom",
  optionsClassName = "max-h-60",
  pyClass = "py-2",
  borderInput,
  alwaysFirstIds = [],
  borderClass = "border-gray-100",

  renderOption,          // (element) => ReactNode
  displayValue,          // (element) => string
  hideXMarkIcon = false,
}) => {
  const [query, setQuery] = useState("");
  const comboboxButtonRef = useRef(null);

  const filteredOptions =
    query === ""
      ? options
      : options.filter((element) =>
          (element?.name || "")
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  const filteredOptionsSorted = [...filteredOptions].sort((a, b) => {
    if (Array.isArray(alwaysFirstIds) && alwaysFirstIds.length) {
      const aIndex = alwaysFirstIds.indexOf(a?.id);
      const bIndex = alwaysFirstIds.indexOf(b?.id);

      const aFirst = aIndex !== -1;
      const bFirst = bIndex !== -1;

      if (aFirst && bFirst) {
        return aIndex - bIndex;
      }

      if (aFirst) return -1;
      if (bFirst) return 1;
    }

    return (a?.name || "").localeCompare(
      b?.name || ""
    );
  });

  const zIndexNumber = useMemo(() => parseZIndex(zIndex), [zIndex]);

  const clearValue = () => {
    setQuery("");
    onChange(null);
  };

  return (
    <div className={`border ${borderClass}`}>
      <Combobox value={value} onChange={onChange}>
        <div className="relative">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none sm:text-sm">
            <Combobox.Input
              className={`w-full border-none ${pyClass} pl-3 ${hideXMarkIcon ? "pr-10" : "pr-16"} text-sm leading-5 text-gray-700 focus:ring-0 placeholder:text-gray-400`}
              displayValue={(el) =>
                displayValue
                  ? displayValue(el)
                  : el?.name
                  ? el.name
                  : ""
              }
              onClick={() => {comboboxButtonRef.current?.click();}}
              onChange={(event) => {
                const v = event.target.value;
                setQuery(v);
                if (v === "") {onChange(null);}
              }}
              placeholder={placeholder}
            />

            {/* Clic su tutta la barra */}
            <Combobox.Button
              type="button"
              className={`absolute inset-0 z-0 h-full w-full cursor-pointer ${borderInput || ""}`}
              aria-label="Apri selezione"
            />

            {/* Controlli a destra */}
            <div className="absolute inset-y-0 right-0 z-20 flex items-center gap-1 pr-2">
              {!hideXMarkIcon && (query !== "" || value) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    clearValue();
                  }}
                  className="rounded-full p-0.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                  title="Cancella selezione"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}

              <Combobox.Button
                ref={comboboxButtonRef}
                type="button"
                className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              >
                <ChevronUpDownIcon className="h-5 w-5" />
              </Combobox.Button>
            </div>
          </div>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options
              style={{ zIndex: zIndexNumber }}
              className={`${positionOptions ? positionOptions : "absolute"} ${
                openDirection === "top" ? "bottom-full mb-1" : "mt-1"
              } ${optionsClassName} w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm ${zIndex}`}
            >
              {filteredOptionsSorted.length === 0 ? (
                <Combobox.Option
                  key="no-results"
                  className="relative cursor-default text-left select-none py-2 pl-6 pr-4 text-gray-700"
                  value={null}
                >
                  Nessun risultato
                </Combobox.Option>
              ) : (
                filteredOptionsSorted.map((element) => (
                  <Combobox.Option
                    key={element.id}
                    className={({ active }) =>
                      `relative cursor-default text-left select-none py-2 pl-6 pr-4 ${
                        active ? `${bgActiveColor} ${textActiveColor}` : "text-gray-700"
                      }`
                    }
                    value={element}
                  >
                    {({ selected, active }) => (
                      <>
                        <div className={`pl-3 pr-2 ${selected ? "font-medium" : "font-normal"}`}>
                          {renderOption ? renderOption(element) : element.name}
                        </div>

                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-indigo-800"
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default ComboboxAutocomplete;
